/**
 * DirectoryReviewsSection — public reviews block dropped into any
 * business detail page (RestaurantDetailPage, later HotelDetailPage etc.).
 *
 * Renders: summary (avg + N + 1..5 breakdown) + submit form + feed.
 * Rating >= 4 auto-deep-links to Google Business Profile if the biz has
 * a `gbp_review_url` recorded.
 */
import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

function Stars({ value, size = 16 }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} width={size} height={size}
              fill={i <= value ? "#f59e0b" : "none"}
              stroke={i <= value ? "#f59e0b" : "#d1d5db"} />
      ))}
    </span>
  );
}

export default function DirectoryReviewsSection({ slug, bizName }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [thanks, setThanks] = useState(null);   // { gbp_url? }

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${slug}/reviews?limit=25`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rating) { setError("Please pick a star rating."); return; }
    setSubmitting(true); setError("");
    try {
      const r = await fetch(`${API}/api/directory/restaurants/${slug}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment, name, email }),
      });
      const j = await r.json();
      if (!r.ok) { setError(j.detail || "Could not submit."); setSubmitting(false); return; }
      setThanks({ gbp_url: j.gbp_url });
      setRating(0); setComment(""); setName(""); setEmail("");
      load();
    } catch (e) { setError(e.message); }
    setSubmitting(false);
  };

  return (
    <section className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="section-reviews">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="font-serif text-lg text-stone-900">Reviews</h2>
          <p className="text-[13px] text-stone-500 mt-0.5">What visitors say about {bizName}</p>
        </div>
        {data && data.count > 0 && (
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <span className="text-2xl font-bold tabular-nums text-stone-900">{data.avg.toFixed(1)}</span>
              <Stars value={Math.round(data.avg)} size={18} />
            </div>
            <div className="text-[12px] text-stone-500 mt-0.5" data-testid="reviews-count">{data.count} review{data.count !== 1 ? "s" : ""}</div>
          </div>
        )}
      </div>

      {/* Breakdown bars */}
      {data && data.count > 0 && (
        <div className="mb-4 space-y-1" data-testid="reviews-breakdown">
          {[5,4,3,2,1].map(n => {
            const c = data.breakdown[String(n)] || 0;
            const pct = data.count ? Math.round(c * 100 / data.count) : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-[12px] text-stone-600">
                <span className="w-8 tabular-nums">{n}★</span>
                <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right tabular-nums text-stone-500">{c}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit form */}
      {!thanks ? (
        <div className="p-4 rounded-xl border border-stone-200 bg-stone-50 mb-5" data-testid="reviews-form">
          <div className="text-[13px] font-medium text-stone-700 mb-2">Leave a review</div>
          <div className="flex items-center gap-1 mb-3" data-testid="reviews-form-rating">
            {[1,2,3,4,5].map(i => (
              <button
                key={i}
                type="button"
                onClick={() => setRating(i)}
                data-testid={`reviews-form-star-${i}`}
                className="p-1 -m-1 hover:scale-110 transition-transform"
                aria-label={`${i} star${i > 1 ? "s" : ""}`}
              >
                <Star width={26} height={26}
                      fill={i <= rating ? "#f59e0b" : "none"}
                      stroke={i <= rating ? "#f59e0b" : "#9ca3af"} />
              </button>
            ))}
          </div>
          <textarea
            data-testid="reviews-form-comment"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder={`Share your experience at ${bizName}…`}
            rows={3}
            className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[14px] resize-y min-h-[64px] focus:outline-none focus:border-stone-400"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <input
              data-testid="reviews-form-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name (optional)"
              className="px-3 py-2 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400"
            />
            <input
              data-testid="reviews-form-email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email (private, optional)"
              type="email"
              className="px-3 py-2 rounded-lg border border-stone-200 text-[14px] focus:outline-none focus:border-stone-400"
            />
          </div>
          {error && <div className="text-[12px] text-rose-600 mt-2" data-testid="reviews-form-error">{error}</div>}
          <button
            data-testid="reviews-form-submit"
            onClick={submit}
            disabled={submitting}
            className="mt-3 w-full sm:w-auto px-5 py-2 rounded-lg bg-stone-900 text-white text-[14px] font-semibold hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Post review"}
          </button>
        </div>
      ) : (
        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-5" data-testid="reviews-thanks">
          <div className="text-[14px] font-semibold text-emerald-900">Thanks for your review!</div>
          {thanks.gbp_url ? (
            <div className="mt-2 text-[13px] text-emerald-800">
              Loved it? Share on Google in 30 seconds —
              <a href={thanks.gbp_url} target="_blank" rel="noreferrer"
                 data-testid="reviews-gbp-cta"
                 className="ml-1 font-semibold underline">Open Google →</a>
            </div>
          ) : (
            <div className="mt-1 text-[13px] text-emerald-800">Your review is now visible below.</div>
          )}
          <button onClick={() => setThanks(null)}
                  className="mt-3 text-[12px] text-emerald-700 underline">
            Post another
          </button>
        </div>
      )}

      {/* Reviews feed */}
      {busy && !data && <div className="text-[13px] text-stone-500">Loading reviews…</div>}
      {data && data.count === 0 && !thanks && (
        <div className="text-[13px] text-stone-500 italic" data-testid="reviews-empty">
          No reviews yet — be the first to review {bizName}.
        </div>
      )}
      {data && data.data && data.data.length > 0 && (
        <div className="space-y-4" data-testid="reviews-list">
          {data.data.map((r, i) => (
            <div key={i} className="pb-4 border-b border-stone-100 last:border-0" data-testid={`review-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Stars value={r.rating} size={14} />
                  <span className="text-[13px] font-medium text-stone-800">{r.display_name}</span>
                </div>
                <span className="text-[11px] text-stone-400 tabular-nums">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                </span>
              </div>
              {r.comment && (
                <p className="text-[14px] text-stone-700 mt-1.5 leading-snug whitespace-pre-line">{r.comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


// Shared moderated review block: HS collects; HustleReviews publishes after approval.
const ModeratedReviewRenderer=lazy(()=>import("../pages/forms/FormRenderer"));
export function ModeratedFormReviews({formSlug,sourceUrl,title="Reviews",language="en"}) {
  const [form,setForm]=useState(null),[data,setData]=useState(null),[error,setError]=useState(""),[writing,setWriting]=useState(false);
  useEffect(()=>{
    let cancelled=false;
    setForm(null);setData(null);setError("");setWriting(false);
    const load=async()=>{
      try {
        const [fr,rr]=await Promise.all([
          fetch(API+"/api/forms/public/"+encodeURIComponent(formSlug),{credentials:"omit"}),
          fetch(API+"/api/forms/public/"+encodeURIComponent(formSlug)+"/reviews?source_url="+encodeURIComponent(sourceUrl),{credentials:"omit"})
        ]);
        if(!fr.ok||!rr.ok)throw new Error("Could not load reviews. Please try again.");
        const [f,d]=await Promise.all([fr.json(),rr.json()]);
        if(!cancelled){setForm(f);setData(d);}
      } catch(e){if(!cancelled)setError(e.message);}
    };
    load();return()=>{cancelled=true;};
  },[formSlug,sourceUrl]);
  return <section data-testid="moderated-reviews" style={{margin:"32px 0"}}>
    <h2>{title}</h2>
    {error&&<p role="alert">{error}</p>}
    {!data&&!error&&<p>Loading reviews…</p>}
    {data&&<><p>{data.count ? data.average+" / 5 · "+data.count+" "+(language==="pl"?"opinii":"reviews"):(language==="pl"?"Jeszcze bez opinii.":"No approved reviews yet.")}</p>
      {data.items.map(review=><article key={review.id} style={{padding:"16px 0",borderBottom:"1px solid #e2e8f0"}}>
        <strong>{review.customer_name}</strong> <Stars value={review.rating}/>
        <p style={{whiteSpace:"pre-wrap"}}>{review.body}</p>
      </article>)}
      <button type="button" onClick={()=>setWriting(v=>!v)} className="pf-btn pf-btn--primary" style={{marginTop:16}}>{writing?(language==="pl"?"Zamknij":"Close"):(language==="pl"?"Dodaj opinię":"Add a review")}</button>
      {writing&&form&&<div style={{marginTop:20}}><p>{language==="pl"?"Wszystkie opinie oczekują na zatwierdzenie przed publikacją.":"All reviews are checked before publication. Your email address stays private."}</p>
        <Suspense fallback={<p>Loading protected form…</p>}><ModeratedReviewRenderer form={form} sourceUrl={sourceUrl}/></Suspense>
      </div>}
    </>}
  </section>;
}
